import React, {useEffect, useState} from 'react';
import moment from 'moment';
import i18n from 'i18next';
import {Avatar, Box, IconButton, Typography} from '@mui/material';
import {Favorite, FavoriteBorderOutlined} from '@mui/icons-material';
import {useTranslate, usePermissions} from 'react-admin';
import {t} from '../themes/statamicTokens';
import PrimaryButton from '../components/statamic/PrimaryButton';
import hasPermission from '../auth/hasPermission';

const CommentItem = ({comment, onReply, onToggleLikeComment, onDelete, isReaction = false}) => {
    const translate = useTranslate();
    const {permissions} = usePermissions();

    const userName = comment.nofam_user_model?.given_name ?? 'Unknown';
    const timePosted = moment(comment.created_time).fromNow();
    const avatarUrl = comment.nofam_user_model?.avatar_thumbnail
        ? `${process.env.REACT_APP_CDN_URL}/${comment.nofam_user_model.avatar_thumbnail}`
        : undefined;

    const [message, setMessage] = useState('');
    const [originalMessage, setOriginalMessage] = useState('');
    const [showOriginal, setShowOriginal] = useState(false);
    const [mine, setMine] = useState(false);

    useEffect(() => {
        setMessage(comment.message);
        setMine(comment.mine);
        if (!comment.mine && comment.translations?.length > 0) {
            for (const tr of comment.translations) {
                if (tr.languageCode === i18n.language) {
                    setMessage(tr.translation);
                    if (!tr.original) setOriginalMessage(comment.message);
                }
            }
        }
    }, [comment]);

    const handleReply = () => {
        if (comment.id) {
            onReply?.(comment.id, showOriginal && originalMessage !== '' ? originalMessage : message);
        }
    };

    const canDelete = hasPermission(permissions, ['delete'], 'post.comment');

    return (
        <Box
            sx={{
                p: 1.5,
                backgroundColor: t.color.surface,
                border: `1px solid ${t.color.border.default}`,
                borderRadius: `${t.radius.md}px`,
                mb: 1,
                ...(isReaction && {
                    ml: 5,
                    borderLeft: `3px solid ${t.color.accent.primary}`,
                }),
            }}
        >
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Avatar
                        src={avatarUrl}
                        alt={userName}
                        sx={{
                            width: 28, height: 28,
                            bgcolor: t.color.sidebarHover,
                            color: t.color.text.secondary,
                            border: `1px solid ${t.color.border.hairline}`,
                            fontSize: 12,
                        }}
                    >
                        {userName.charAt(0)}
                    </Avatar>
                    <Typography
                        sx={{
                            fontSize: `${t.type.bodyStrong.size}px`,
                            fontWeight: 600,
                            color: t.color.text.primary,
                        }}
                    >
                        {userName}
                    </Typography>
                    <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                        · {timePosted}
                    </Typography>
                </Box>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.25}}>
                    <IconButton
                        size="small"
                        onClick={() => onToggleLikeComment?.(comment)}
                        sx={{p: 0.5, color: comment.ilike ? t.color.status.dangerSolid : t.color.text.muted}}
                    >
                        {comment.ilike
                            ? <Favorite sx={{fontSize: 16}}/>
                            : <FavoriteBorderOutlined sx={{fontSize: 16}}/>}
                    </IconButton>
                    <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary, minWidth: 12}}>
                        {comment.likes ?? 0}
                    </Typography>
                </Box>
            </Box>

            <Typography
                sx={{
                    fontSize: `${t.type.body.size}px`,
                    color: t.color.text.primary,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    mb: 1,
                }}
            >
                {showOriginal && originalMessage !== '' ? originalMessage : message}
            </Typography>

            <Box sx={{display: 'flex', gap: 0.5}}>
                {!mine && !isReaction && (
                    <PrimaryButton
                        variant="ghost"
                        onClick={handleReply}
                        sx={{minHeight: 26, px: 1, fontSize: `${t.type.caption.size}px`}}
                    >
                        {translate('Reply')}
                    </PrimaryButton>
                )}
                {!mine && originalMessage && (
                    <PrimaryButton
                        variant="ghost"
                        onClick={() => setShowOriginal(v => !v)}
                        sx={{minHeight: 26, px: 1, fontSize: `${t.type.caption.size}px`}}
                    >
                        {showOriginal ? translate('Show translation') : translate('Show original')}
                    </PrimaryButton>
                )}
                {canDelete && (
                    <PrimaryButton
                        variant="ghost"
                        onClick={() => onDelete?.(comment.id)}
                        sx={{
                            minHeight: 26, px: 1,
                            fontSize: `${t.type.caption.size}px`,
                            color: t.color.status.dangerSolid,
                        }}
                    >
                        {translate('Delete')}
                    </PrimaryButton>
                )}
            </Box>
        </Box>
    );
};

export default CommentItem;
